import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  Sparkles,
  Gauge,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Lightbulb,
  ScrollText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "@/lib/i18n";
import { Disclaimer } from "@/components/Disclaimer";
import { TransparencyPanel } from "@/components/TransparencyPanel";

/**
 * LockedPreviewCard — Phase 8.LP.FLE (Free-limit exhausted polish)
 * ----------------------------------------------------------------
 * Renders the Free-quota-exhausted preview surface for /analyze.
 *
 * The exact valuation values (Fair Value, Opportunity Score,
 * Upside/Downside, Bull/Bear Case, Key Risks, full AI narrative,
 * Valuation Breakdown) are STRIPPED SERVER-SIDE in
 * ``build_locked_preview`` — this component never receives them
 * and never fabricates them. We only render:
 *
 *   1. A context-aware status hero:
 *      - "Free analysis limit reached" headline
 *      - "You've used all {used} of {limit} free analyses in this
 *        period." subheadline driven by ``data.usage`` (10/10 in the
 *        first 30d, 5/5 in rolling — never hardcoded)
 *      - Either a concrete reset date (from ``usage.next_reset_at``)
 *        or a safe generic "resets with your rolling 30-day window"
 *        hint when the backend cannot determine the exact date
 *      - "Upgrade to Pro to continue instantly." tagline
 *      - Primary CTA "Upgrade to Pro" + secondary CTA "See plans"
 *
 *   2. A grid of colored locked-section cards (one per
 *      ``locked_sections`` id) with subtle section-accent borders
 *      and icons so the page reads like a premium terminal rather
 *      than a flat gray fallback.
 *
 *   3. A single educational disclaimer at the bottom.
 *
 * Deliberately does NOT use `blur-*` classes to obscure numbers —
 * we render generic placeholders instead. This makes the "no
 * exact values leaked" guarantee visible in the DOM (nothing to
 * un-blur via devtools) rather than relying on CSS alone.
 */

// ─── Locked-card accent registry ───────────────────────────────
// Each entry drives a subtle border + icon tint + lock chip color
// so the preview looks like a colored terminal instead of a flat
// gray fallback. Colors are intentionally muted — accents, not
// backgrounds — to preserve the dark premium chrome.
const SECTION_ACCENTS = {
  fair_value: {
    icon: BarChart3,
    ring: "border-cyan-500/30",
    glow: "shadow-[0_0_0_1px_rgba(34,211,238,0.20)]",
    chip: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40",
    iconTile: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  },
  opportunity_score: {
    icon: Gauge,
    ring: "border-sky-500/30",
    glow: "shadow-[0_0_0_1px_rgba(56,189,248,0.20)]",
    chip: "bg-sky-500/10 text-sky-300 border-sky-500/40",
    iconTile: "bg-sky-500/10 border-sky-500/30 text-sky-300",
  },
  upside_downside: {
    icon: ArrowUpRight,
    ring: "border-cyan-500/30",
    glow: "shadow-[0_0_0_1px_rgba(34,211,238,0.20)]",
    chip: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40",
    iconTile: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  },
  bull_case: {
    icon: TrendingUp,
    ring: "border-emerald-500/30",
    glow: "shadow-[0_0_0_1px_rgba(16,185,129,0.20)]",
    chip: "bg-emerald-500/10 text-emerald-300 border-emerald-500/40",
    iconTile: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  },
  bear_case: {
    icon: TrendingDown,
    ring: "border-rose-500/30",
    glow: "shadow-[0_0_0_1px_rgba(244,63,94,0.20)]",
    chip: "bg-rose-500/10 text-rose-300 border-rose-500/40",
    iconTile: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  },
  key_risks: {
    icon: AlertTriangle,
    ring: "border-amber-500/30",
    glow: "shadow-[0_0_0_1px_rgba(245,158,11,0.20)]",
    chip: "bg-amber-500/10 text-amber-300 border-amber-500/40",
    iconTile: "bg-amber-500/10 border-amber-500/30 text-amber-300",
  },
  ai_narrative: {
    icon: ScrollText,
    ring: "border-violet-500/30",
    glow: "shadow-[0_0_0_1px_rgba(139,92,246,0.20)]",
    chip: "bg-violet-500/10 text-violet-300 border-violet-500/40",
    iconTile: "bg-violet-500/10 border-violet-500/30 text-violet-300",
  },
  valuation_breakdown: {
    icon: Lightbulb,
    ring: "border-cyan-500/30",
    glow: "shadow-[0_0_0_1px_rgba(34,211,238,0.20)]",
    chip: "bg-cyan-500/10 text-cyan-300 border-cyan-500/40",
    iconTile: "bg-cyan-500/10 border-cyan-500/30 text-cyan-300",
  },
};

const DEFAULT_SECTION_IDS = Object.keys(SECTION_ACCENTS);

function formatResetDate(iso, locale) {
  if (!iso || typeof iso !== "string") return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const lang = locale === "de" ? "de-DE" : "en-US";
    return new Intl.DateTimeFormat(lang, { dateStyle: "long" }).format(d);
  } catch {
    return null;
  }
}

function LockedTile({ id, t }) {
  const accent = SECTION_ACCENTS[id] || SECTION_ACCENTS.fair_value;
  const Icon = accent.icon;
  const title = t(`analyze.locked_preview.sections.${id}.title`);
  const hint = t(`analyze.locked_preview.sections.${id}.hint`);
  return (
    <Card
      className={`bg-card/50 backdrop-blur-sm border ${accent.ring} ${accent.glow}`}
      data-testid={`locked-card-${id}`}
      data-accent={id}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className={`shrink-0 h-9 w-9 rounded-xl border flex items-center justify-center ${accent.iconTile}`}
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 border text-[10px] font-semibold uppercase tracking-wider ${accent.chip}`}
            >
              <Lock className="h-2.5 w-2.5" aria-hidden />
              {t("analyze.locked_preview.locked_label")}
            </div>
            <div
              className="mt-1.5 font-display text-base sm:text-lg font-medium tracking-tight truncate"
              data-testid={`locked-card-${id}-title`}
            >
              {title}
            </div>
            <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {hint}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function LockedPreviewCard({ data, className = "" }) {
  const navigate = useNavigate();
  const { t, locale } = useTranslation();
  if (!data) return null;

  const sections =
    Array.isArray(data.locked_sections) && data.locked_sections.length > 0
      ? data.locked_sections
      : DEFAULT_SECTION_IDS;

  const upgrade = data.upgrade || {};
  const usage = data.usage || {};

  // Numbers are read straight from the server payload so the "X of X"
  // line stays context-aware (10/10 first-period, 5/5 rolling). If
  // for any reason the payload is malformed, we fall back to the
  // legacy generic body copy from `upgrade.body` instead of showing
  // an incorrect number.
  const used = Number.isFinite(usage.used) ? usage.used : null;
  const limit = Number.isFinite(usage.limit) ? usage.limit : null;
  const hasQuotaContext = used != null && limit != null;

  const resetDate = formatResetDate(usage.next_reset_at, locale);

  const headline = t("analyze.locked_preview.headline") || upgrade.headline;
  const subheadline = hasQuotaContext
    ? t("analyze.locked_preview.subheadline", { used, limit })
    : upgrade.body || "";
  const resetLine = resetDate
    ? t("analyze.locked_preview.reset_with_date", { date: resetDate })
    : t("analyze.locked_preview.reset_generic");
  const upgradeLine = t("analyze.locked_preview.upgrade_line");
  const ctaPrimary = t("analyze.locked_preview.cta_primary") || upgrade.cta_label || "Upgrade to Pro";
  const ctaSecondary = t("analyze.locked_preview.cta_secondary") || upgrade.secondary_cta_label || "See plans";
  const disclaimer = t("analyze.locked_preview.disclaimer") || upgrade.disclaimer;

  return (
    <section
      className={`space-y-5 ${className}`}
      data-testid="locked-preview-root"
      aria-label="Locked analysis preview"
    >
      {/* Hero / status card */}
      <div
        className="relative overflow-hidden rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/10 via-card/60 to-card/60 p-5 sm:p-7"
        data-testid="locked-preview-header"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-primary/15 blur-3xl"
        />
        <div className="relative flex items-start gap-4">
          <div
            className="shrink-0 h-11 w-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center"
            aria-hidden
          >
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              className="font-display text-xl sm:text-2xl font-semibold tracking-tight"
              data-testid="locked-preview-headline"
            >
              {headline}
            </h2>
            {subheadline ? (
              <p
                className="mt-2 text-sm sm:text-[15px] text-foreground/85 leading-relaxed"
                data-testid="locked-preview-subheadline"
              >
                {subheadline}
              </p>
            ) : null}
            <p
              className="mt-1.5 text-xs sm:text-sm text-muted-foreground leading-relaxed"
              data-testid="locked-preview-reset-line"
              data-reset-source={resetDate ? "date" : "generic"}
            >
              {resetLine}
            </p>
            <p
              className="mt-3 text-sm sm:text-[15px] text-foreground/95"
              data-testid="locked-preview-upgrade-line"
            >
              {upgradeLine}
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center">
              <Button
                size="lg"
                onClick={() => navigate("/billing")}
                data-testid="locked-preview-upgrade-cta"
              >
                <Sparkles className="h-4 w-4 mr-1.5" />
                {ctaPrimary}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/pricing")}
                data-testid="locked-preview-see-plans-cta"
              >
                {ctaSecondary}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Colored locked section cards */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4"
        data-testid="locked-preview-cards"
      >
        {sections.map((id) => (
          <LockedTile key={id} id={id} t={t} />
        ))}
      </div>

      {/* WS-1 canonical short-form disclaimer (in addition to any
          locked-preview-specific note). Provisional product wording;
          final legal wording is WS-11. */}
      <div className="pt-2">
        <Disclaimer compact />
      </div>

      {/* Preview-specific disclaimer (from i18n / API payload). */}
      <p
        className="text-xs text-muted-foreground"
        data-testid="locked-preview-disclaimer"
      >
        {disclaimer}
      </p>

      {data?.ws7_metadata && (
        <div className="pt-2" data-testid="locked-preview-ws7">
          <TransparencyPanel metadata={data.ws7_metadata} compact />
        </div>
      )}
    </section>
  );
}

export default LockedPreviewCard;
